import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import styles from "./Payment.module.scss";
import Loader from "../../components/loader/Loader";
import Button from "../../components/button/Button"; // Using custom button
import { routesConstants } from "../../constants";
import Logo from "../../assets/Logo.png";
import { getTransactionStatus } from "../../services/user.service";
import { get } from "lodash";
import axios from "../../axios";

const Payment = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  const transactionId = searchParams.get("transactionId");
  const checkout = useMemo(() => {
    const fromState = get(location, "state.checkout", null);
    if (fromState) return fromState;
    if (!transactionId) return null;
    try {
      const raw = sessionStorage.getItem(`billing:checkout:${transactionId}`);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }, [location, transactionId]);

  const paymentUrl = useMemo(() => {
    return searchParams.get("paymentUrl") || get(checkout, "url", "");
  }, [searchParams, checkout]);

  const pollIntervalMs = useMemo(() => {
    const v = get(checkout, "poll.pollIntervalMs", null);
    return typeof v === "number" && v > 0 ? v : 2000;
  }, [checkout]);

  useEffect(() => {
    try {
      if (transactionId && checkout) {
        sessionStorage.setItem(
          `billing:checkout:${transactionId}`,
          JSON.stringify(checkout)
        );
      }
    } catch (e) {
      // ignore
    }
  }, [transactionId, checkout]);

  useEffect(() => {
    // Once the iframe is started, poll in the background and route to /payment-processing
    // when the transaction is no longer PENDING (SUCCESS/FAILED).
    if (!transactionId || !isSubmitted) return;

    const intervalId = setInterval(async () => {
      try {
        const statusUrl = get(checkout, "poll.transactionStatusUrl", null);
        const res = statusUrl
          ? await axios.get(statusUrl)
          : await getTransactionStatus(transactionId);
        const paymentStatus =
          res?.data?.data?.payment_status ||
          res?.data?.payment_status ||
          res?.data?.status ||
          "PENDING";

        if (paymentStatus && paymentStatus !== "PENDING") {
          clearInterval(intervalId);
          navigate(
            `${routesConstants.ROUTES.PAYMENT_PROCESSING}?transactionId=${encodeURIComponent(
              transactionId
            )}`,
            { replace: true, state: { checkout } }
          );
        }
      } catch (e) {
        // ignore; keep polling
      }
    }, pollIntervalMs);

    return () => clearInterval(intervalId);
  }, [transactionId, isSubmitted, pollIntervalMs, navigate, checkout]);

  useEffect(() => {
    if (paymentUrl && formRef.current && !isSubmitted) {
      // Parse URL
      const url = new URL(paymentUrl);
      const baseUrl = url.origin + url.pathname;
      const params = new URLSearchParams(url.search);

      // Add or update styling params
      params.set("trBgColor", "ffffff"); // White background
      params.set("trTextColor", "000000"); // Black text
      params.set("trButtonColor", "6b3eb4"); // Purple button from theme
      params.set("nologo", "1"); // Remove logo
      params.set("buttonLabel", "Pay Now");

      // Set form fields
      Array.from(params.entries()).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        formRef.current.appendChild(input);
      });

      // Submit form to load iframe
      formRef.current.action = baseUrl;
      formRef.current.target = "paymentFrame";
      formRef.current.submit();
      setIsSubmitted(true);
    }
  }, [paymentUrl, isSubmitted]);

  if (!paymentUrl) {
    return (
      <div className={styles.Payment}>
        <div className={styles.header}>
          <img src={Logo} alt="4Play" className={styles.logo} />
        </div>
        <div className={styles.errorContainer}>
          <h2>No payment URL provided.</h2>
          <Button onClick={() => navigate(routesConstants.ROUTES.PACKAGES)}>
            Back to Plans
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.Payment}>
      <div className={styles.container1}>
        <div className={styles.header}>
          <img src={Logo} alt="4Play" className={styles.logo} />
        </div>
        <div className={styles.mainContent}>
          <div className={styles.titleContainer}>
            <h1 className={styles.title}>Secure Checkout</h1>
            <p className={styles.subtitle}>
              Complete your payment securely. All transactions are encrypted.
            </p>
            <p className={styles.subtitle} style={{ marginTop: 12 }}>
              After you submit payment, we’ll confirm it automatically.
            </p>
          </div>
          <div className={styles.container}>
            {!isIframeLoaded && (
              <div className={styles.loader}>
                <Loader />
              </div>
            )}
            <form ref={formRef} className={styles.form} method="POST">
              {/* Hidden form for POST to iframe */}
            </form>
            <div
              className={styles.iframeWrapper}
              style={{ visibility: isIframeLoaded ? "visible" : "hidden" }}
            >
              <iframe
                name="paymentFrame"
                className={styles.iframeContent}
                src="about:blank"
                allowPaymentRequest="true"
                title="Payment"
                onLoad={() => {
                  setIsIframeLoaded(true);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
